import axios from 'axios'
import appEnv from '@/support/env'
import {
  RATE_LIMITED_CODE,
  RATE_LIMIT_STATUS,
  noteRateLimited,
  rateLimitMessage,
  rateLimitedError,
  rateLimitedFor,
  serverErrorMessage
} from '@/shared/api'

export const apiUrl = () => {
  return appEnv.BASE_API
}

const apiClient = axios.create({
  baseURL: apiUrl(),
  timeout: 60000,
  withCredentials: true
})

// Request interceptors
apiClient.interceptors.request.use(
  (config) => {
    // The same cooldown the new instance obeys, and deliberately the *same* window: both
    // instances talk to one backend and spend one budget, so a 429 earned by the reports
    // panel has to stop the dashboard's six cards too. Sharing the module is what makes
    // that true — unlike the 401 rule below, which is copied into both files.
    if (rateLimitedFor() > 0) return Promise.reject(rateLimitedError(config))

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptors
apiClient.interceptors.response.use(
  (response) => {
    return Promise.resolve(response)
  },
  async (error) => {
    if (!error.response || error.code === RATE_LIMITED_CODE) {
      // A request this instance never sent, because the cooldown below was already open.
      // The user was told when it opened; nothing more to say per request held back.
      return Promise.reject(error)
    } else if (error.response.status === RATE_LIMIT_STATUS) {
      // Told once per window rather than once per request: this instance toasts every
      // failure that is not a 400, and a dashboard refused six times over drew six
      // identical toasts about being asked too often.
      const opensWindow = noteRateLimited(error)

      if (opensWindow && error.config?.skipGlobalErrorHandler !== true) {
        const { ElMessage } = await import('element-plus')
        ElMessage({
          message: rateLimitMessage(),
          type: 'warning',
          duration: 8000
        })
      }

      return Promise.reject(error)
    } else if (error.response.status === 401) {
      const authRoutes = [
        'login',
        'registration',
        'restore',
        'confirmation',
        'reset_password',
        'bcm'
      ]

      const isSafePage =
        authRoutes.some(
          (path) =>
            window.location.pathname.includes(path) ||
            window.location.hash.includes(path)
        ) ||
        (window.location.pathname === '/' && !window.location.hash) ||
        window.location.hash === '#/'

      if (!isSafePage) {
        window.location.href = '/'
      }

      return Promise.reject(error)
    } else {
      // `error.message` is axios's own line — "Request failed with status code 422" —
      // which tells the user nothing and is what this used to show. The server sends a
      // sentence explaining the refusal; `serverErrorMessage` reads all six shapes the
      // API wraps it in, and falls back only when the body has nothing to say.
      //
      // `skipGlobalErrorHandler` is honoured here for the same reason the new instance
      // honours it: a caller that reports the failure itself would otherwise produce two
      // toasts about one refusal. Nothing set it on this instance before, so adding the
      // check changes no existing screen.
      if (
        error.response.status !== 400 &&
        error.config?.skipGlobalErrorHandler !== true
      ) {
        const { ElMessage } = await import('element-plus')
        ElMessage({
          message: serverErrorMessage(
            error,
            'A server error occurred. Please try again later.'
          ),
          type: 'error',
          duration: 5 * 1000
        })
      }
      return Promise.reject(error)
    }
  }
)

export default apiClient
